import React, { useState, useRef, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    IconButton,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    CameraAlt as CameraIcon,
    FlipCameraIos as FlipCameraIcon,
    Upload as UploadIcon
} from '@mui/icons-material';

export default function CameraCapture({ open, onClose, onCapture }) {
    const [stream, setStream] = useState(null);
    const [error, setError] = useState('');
    const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
    const [isInitializing, setIsInitializing] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        
        if (open && !isInitializing) {
            // Delay to ensure previous stream is fully released
            const timer = setTimeout(() => {
                if (isMounted) {
                    startCamera();
                }
            }, 100);
            return () => {
                clearTimeout(timer);
                isMounted = false;
            };
        } else if (!open) {
            stopCamera();
        }

        return () => {
            isMounted = false;
            stopCamera();
        };
    }, [open, facingMode]);

    const startCamera = async () => {
        if (isInitializing) return;
        
        try {
            setIsInitializing(true);
            setError('');
            
            // First stop any existing stream
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => {
                    track.stop();
                });
                streamRef.current = null;
            }
            
            // Check if camera is available
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setError('Camera is not available on this device');
                setIsInitializing(false);
                return;
            }

            // Check permission status if available
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const permissionStatus = await navigator.permissions.query({ name: 'camera' });
                    if (permissionStatus.state === 'denied') {
                        setError('Camera permission is denied. Please enable camera access in your browser settings.');
                        setIsInitializing(false);
                        return;
                    }
                } catch (permErr) {
                    // Permissions API not supported, continue anyway
                    console.log('Permissions API not supported');
                }
            }

            // Adjust constraints for mobile devices
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const constraints = {
                video: {
                    facingMode: facingMode,
                    width: { ideal: isMobile ? 1280 : 1920 },
                    height: { ideal: isMobile ? 720 : 1080 }
                }
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = mediaStream;
            setStream(mediaStream);
            
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                // Ensure video plays
                videoRef.current.play().catch(e => console.log('Video play failed:', e));
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError('Camera access was denied. Please allow camera access and try again.');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setError('No camera found on this device.');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                setError('Camera is already in use by another application. Please close other apps using the camera and try again.');
            } else if (err.name === 'OverconstrainedError') {
                setError('Camera does not support the requested settings. Trying default settings...');
                // Try again with basic constraints
                try {
                    const basicStream = await navigator.mediaDevices.getUserMedia({ video: true });
                    streamRef.current = basicStream;
                    setStream(basicStream);
                    if (videoRef.current) {
                        videoRef.current.srcObject = basicStream;
                    }
                    setError(''); // Clear error if basic constraints work
                } catch (basicErr) {
                    setError('Failed to access camera with any settings.');
                }
            } else {
                setError(`Failed to access camera: ${err.message || 'Unknown error'}`);
            }
        } finally {
            setIsInitializing(false);
        }
    };

    const stopCamera = () => {
        // Stop both state stream and ref stream
        if (stream) {
            stream.getTracks().forEach(track => {
                track.stop();
            });
            setStream(null);
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => {
                track.stop();
            });
            streamRef.current = null;
        }
        // Clear video element
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    const buildPhotoPayload = (file, blobUrl, dataUrl) => ({
        file,
        dataUrl,
        url: blobUrl || URL.createObjectURL(file),
        name: file.name,
        timestamp: new Date().toISOString()
    });

    const handleCapture = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');

            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Draw video frame to canvas
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Convert to blob
            canvas.toBlob((blob) => {
                if (blob) {
                    // Create a File object from the blob
                    const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });

                    // Create data URL for preview
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        const photoData = buildPhotoPayload(file, URL.createObjectURL(blob), e.target.result);
                        onCapture(photoData);
                        handleClose();
                    };
                    reader.readAsDataURL(blob);
                }
            }, 'image/jpeg', 0.9);
        }
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const photoData = buildPhotoPayload(file, null, e.target.result);
            onCapture(photoData);
            handleClose();
        };
        reader.readAsDataURL(file);

        // Reset input so the same file can be re-selected later
        event.target.value = '';
    };

    const handleFlipCamera = async () => {
        if (isInitializing) return;
        
        // Stop current stream completely before switching
        stopCamera();
        
        // Add small delay to ensure camera is released
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Switch facing mode
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    };

    const handleClose = () => {
        stopCamera();
        setError('');
        setIsInitializing(false);
        onClose();
    };
    
    const handleRetry = () => {
        setError('');
        startCamera();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '90vh', maxHeight: 800 }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Take Photo</Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: 'relative', backgroundColor: '#000' }}>
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                {error ? (
                    <Box sx={{ 
                        p: 3, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '100%',
                        gap: 2
                    }}>
                        <Alert severity="error" sx={{ maxWidth: 400 }}>
                            {error}
                        </Alert>
                        <Button 
                            variant="contained" 
                            onClick={handleRetry}
                            disabled={isInitializing}
                        >
                            {isInitializing ? 'Initializing...' : 'Try Again'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<UploadIcon />}
                            onClick={handleUploadClick}
                        >
                            Upload from device
                        </Button>
                    </Box>
                ) : (
                    <>
                        {isInitializing && (
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <CircularProgress size={60} sx={{ color: 'white' }} />
                                <Typography variant="body1" sx={{ color: 'white' }}>
                                    Initializing camera...
                                </Typography>
                            </Box>
                        )}
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                opacity: isInitializing ? 0.3 : 1
                            }}
                        />
                        <canvas
                            ref={canvasRef}
                            style={{ display: 'none' }}
                        />

                        <Box
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                display: 'flex',
                                gap: 1,
                                zIndex: 10
                            }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<UploadIcon />}
                                onClick={handleUploadClick}
                                sx={{
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                                }}
                            >
                                Upload
                            </Button>
                        </Box>
                        
                        {/* Camera controls overlay */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: 20,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 2
                        }}>
                            <IconButton
                                onClick={handleFlipCamera}
                                disabled={isInitializing || !stream}
                                sx={{
                                    backgroundColor: 'rgba(255,255,255,0.3)',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        color: 'rgba(255,255,255,0.3)'
                                    }
                                }}
                            >
                                <FlipCameraIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleCapture}
                                disabled={!stream || isInitializing}
                                sx={{
                                    backgroundColor: 'white',
                                    color: 'primary.main',
                                    width: 64,
                                    height: 64,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.9)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(255,255,255,0.3)'
                                    }
                                }}
                            >
                                <CameraIcon fontSize="large" />
                            </IconButton>
                        </Box>
                    </>
                )}
            </DialogContent>

            {error && (
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            )}
        </Dialog>
    );
}
